<!--
  The station switcher.

  Only rendered when the account can reach more than one, because a control that offers
  a single choice is a label pretending to be a button. Switching reloads the page
  rather than re-fetching in place: every open screen, every poll and every cached list
  belongs to the station that was current when it loaded, and a full reload is the one
  way to be certain none of them is left showing the previous one.
-->
<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ICONS } from '@shiocast/ui';
import { useStationsStore } from '@/stores/stations';

const { t } = useI18n();
const stations = useStationsStore();
const open = ref(false);

function choose(id: string): void {
  open.value = false;
  if (id === stations.currentId) return;
  stations.select(id);
  window.location.reload();
}
</script>

<template>
  <div v-if="stations.hasChoice" class="switcher">
    <button
      class="shio-btn shio-btn--ghost switcher__trigger"
      type="button"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :title="t('stations.switch')"
      @click="open = !open"
    >
      <component :is="ICONS.Radio" :size="16" aria-hidden="true" />
      <span class="switcher__name">{{ stations.current?.name ?? t('stations.none') }}</span>
      <component :is="ICONS.ChevronRight" :size="14" class="switcher__chevron" aria-hidden="true" />
    </button>

    <!-- A click anywhere else closes it, which is what a person expects of a menu. -->
    <div v-if="open" class="switcher__scrim" @click="open = false" />

    <ul v-if="open" class="switcher__menu shio-panel" role="listbox">
      <li v-for="station in stations.stations" :key="station.id">
        <button
          class="switcher__option"
          type="button"
          role="option"
          :aria-selected="station.id === stations.currentId"
          @click="choose(station.id)"
        >
          <span class="switcher__option-name">{{ station.name }}</span>
          <span class="shio-body-xs switcher__option-org">{{ station.organisation.name }}</span>
          <component
            v-if="station.id === stations.currentId"
            :is="ICONS.Check"
            :size="14"
            aria-hidden="true"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.switcher {
  position: relative;
}

.switcher__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--shio-space-2);
  max-width: 15rem;
}

.switcher__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switcher__chevron {
  transform: rotate(90deg);
  opacity: 0.6;
}

/* Behind the menu and over everything else, so the next click closes rather than acts. */
.switcher__scrim {
  position: fixed;
  inset: 0;
  z-index: var(--shio-z-sticky);
}

.switcher__menu {
  position: absolute;
  top: calc(100% + var(--shio-space-2));
  right: 0;
  z-index: var(--shio-z-banner);
  min-width: 16rem;
  max-height: 60vh;
  overflow-y: auto;
  padding: var(--shio-space-1);
  margin: 0;
  list-style: none;
}

.switcher__option {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'name check' 'org check';
  align-items: center;
  gap: 0 var(--shio-space-2);
  width: 100%;
  padding: var(--shio-space-2);
  background: none;
  border: 0;
  border-radius: var(--shio-radius-sm);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.switcher__option:hover,
.switcher__option:focus-visible {
  background: hsl(var(--shio-surface-hover));
}

.switcher__option-name {
  grid-area: name;
}

.switcher__option-org {
  grid-area: org;
  opacity: 0.7;
}
</style>
